import {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {literal, type} from 'io-ts';
import {Jwt, JwtManager, SHARED_MUTEX, type RefreshedTokenData} from './JwtManager';

export interface AxiosRefreshOptions {
    jwtManager: JwtManager;
    axios: AxiosInstance;
}

export const createAxiosWithRefresh = (options: AxiosRefreshOptions) => {
    const axios = options.axios;
    const jwtManager = options.jwtManager;
    const mutex = SHARED_MUTEX;

    const AxiosError401 = type({
        response: type({status: literal(401)}),
    });

    const axiosWithRefresh = async (config: AxiosRequestConfig): Promise<AxiosResponse> => {
        await mutex.waitForUnlock();
        let response: AxiosResponse;
        try {
            response = await axios(config);
        } catch (error: unknown) {
            if (!AxiosError401.is(error)) {
                throw error;
            }
            if (!mutex.isLocked()) {
                const release = await mutex.acquire();
                try {
                    const refreshed: RefreshedTokenData | null = await jwtManager.fetchNewRefreshToken();
                    if (!refreshed) {
                        throw error;
                    }
                    jwtManager.persistJwt(refreshed.access_token);
                    jwtManager.persistRefreshToken(refreshed.refresh_token);
                    response = await axios(updateAxiosRequest(refreshed.access_token, config));
                } finally {
                    release();
                }
            } else {
                await mutex.waitForUnlock();
                const token = jwtManager.getJwt();
                if (!token) {
                    throw error;
                }
                response = await axios(updateAxiosRequest(token, config));
            }
        }
        return response;
    };

    const updateAxiosRequest = (token: Jwt, config: AxiosRequestConfig): AxiosRequestConfig => {
        if (config.headers && 'Authorization' in config.headers) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    };

    return axiosWithRefresh;
};
